import React, { useContext, useState } from 'react';
import T from 'prop-types';
import { get } from 'lodash-es';

const LocaleContext = React.createContext(null);
const TextScopeContext = React.createContext({ scope: [] });
const OptionalContext = React.createContext({ isOptional: false });

const mkPath = (scope, path) => [
  ...(typeof scope === 'string' ? scope.split('.') : scope),
  ...(typeof path === 'string' ? path.split('.') : path),
];

export const Locale = ({ translations, culture, ...rest }) => (
  <LocaleContext.Provider value={{ translations, culture }} {...rest} />
);

Locale.propTypes = {
  translations: T.object,
  culture: T.object,
};

const getTranslationsKeyPath = (scope, path, absolutePath) =>
  absolutePath || mkPath(scope, path === '.' ? [] : path);

const getTranslations = (translations, scope) => (path, absolutePath) => {
  if (path || absolutePath) {
    const keyPath = getTranslationsKeyPath(scope, path, absolutePath);

    return absolutePath === '.'
      ? translations
      : get(translations, keyPath, null);
  }

  return null;
};

export const Translation = ({
  fallbackPath,
  path,
  fallbackAbsolutePath,
  absolutePath,
  children,
}) => (
  <LocaleContext.Consumer>
    {({ translations }) => (
      <TextScopeContext.Consumer>
        {({ scope }) => {
          const translationsFor = getTranslations(translations, scope);
          const translationValue =
            translationsFor(path, absolutePath) ||
            translationsFor(fallbackPath, fallbackAbsolutePath);

          if (!translationValue) {
            return (
              <OptionalContext.Consumer>
                {({ isOptional, setDisplayChildren }) => {
                  if (!isOptional) {
                    const keyPath = getTranslationsKeyPath(
                      scope,
                      path,
                      absolutePath
                    );
                    throw new Error(
                      `MissingTranslation: {${[].concat(keyPath).join('.')}}`
                    );
                  } else {
                    setDisplayChildren(false);
                    return null;
                  }
                }}
              </OptionalContext.Consumer>
            );
          }

          if (typeof children === 'function') {
            return children(translationValue);
          }

          if (typeof translationValue === 'string') {
            return translationValue;
          }

          return null;
        }}
      </TextScopeContext.Consumer>
    )}
  </LocaleContext.Consumer>
);

Translation.propTypes = {
  fallbackPath: T.string,
  fallbackAbsolutePath: T.string,
  path: T.oneOfType([T.string, T.arrayOf(T.string)]),
  absolutePath: T.string,
  children: T.oneOfType([T.func, T.node]),
};

export const Translations = ({ children }) => {
  if (typeof children !== 'function') {
    throw new Error('<Translations /> requires a render prop.');
  }

  return <LocaleContext.Consumer>{children}</LocaleContext.Consumer>;
};

Translations.propTypes = {
  children: T.func.isRequired,
};

export const TranslationScope = ({ path, ...rest }) => (
  <TextScopeContext.Provider value={{ scope: path }} {...rest} />
);

TranslationScope.propTypes = {
  path: T.oneOfType([T.string, T.arrayOf(T.string)]),
};

export const Optional = (props) => {
  const [displayChildren, setDisplayChildren] = useState(true);
  return (
    <>
      {displayChildren ? (
        <OptionalContext.Provider
          value={{ isOptional: true, setDisplayChildren }}
          {...props}
        />
      ) : null}
    </>
  );
};

export const useTranslations = () => {
  const locale = useContext(LocaleContext);
  return locale && locale.translations ? locale.translations : {};
};

export const useCultureConfiguration = () => {
  const locale = useContext(LocaleContext);
  return locale && locale.culture ? locale.culture : {};
};
